import {
  apiAddEventParticipantsByEmails,
  apiAddEventParticipantsByFullNames,
  apiCreateEvent,
  apiDeleteEvent,
  apiDeleteEventParticipant,
  apiEditEvent,
  apiEditEventAttachment,
  apiEventParticipants,
  apiMyEventDetails,
  apiMyEventsList,
  apiMyEventsNextPageCount,
  apiMyReportsList,
  apiMyReportsNextPageCount,
  apiPublicEventDetails,
  apiPublicEventsList,
  apiPublicEventsNextPageCount,
  apiRegisterToEventAsGuest,
  apiRegisterToEventAsUser,
  apiSendEventDetails,
  apiSendInviteToEvent,
  apiValidateEventParticipants,
} from '@/utils/apiUrls';
import { EventsTimePeriods } from '@/constants/events';
import {
  AddingEventParticipantsByFullNamesPayload,
  CheckingParticipantsEligibilityResponse,
  CreateEventResponse,
  EventAttachmentUpdateResponse,
  EventDetailsResponse,
  EventParticipantsResponse,
  EventsListResponse,
  EventsNextPageCountResponse,
  RegisterToEventPayload,
  UpdateEventResponse,
} from '@/types/events';
import { SearchParams as SearchParamsType, UpdateAttachmentPayload } from '@/types/common';
import { Dispatch, SetStateAction } from 'react';
import { useCommonFetchers } from '@/hooks/useCommonFetchers';
import { useAuth0 } from '@auth0/auth0-react';
import { FixEventFormValues } from '@/src/components/Events/modals/FixEventModal';
import { ErrorWithMessageAndResponseStatus, SearchParams } from '@/constants/common';

export const useEventsFetchers = () => {
  const { fetcher, uploadFileToS3 } = useCommonFetchers();
  const { isAuthenticated } = useAuth0();

  const fetchEvents = async (
    eventsOffset: number,
    eventsLimit: number,
    filter: SearchParamsType,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    signal: AbortSignal,
    timePeriod?: EventsTimePeriods,
    activityType?: string,
  ): Promise<EventsListResponse> => {
    const reported = filter[SearchParams.filterReported] ? parseInt(filter[SearchParams.filterReported]) : false;

    const apiPath = timePeriod
      ? reported === 1
        ? apiMyReportsList(eventsOffset.toString(), eventsLimit.toString(), timePeriod, activityType, filter)
        : apiMyEventsList(eventsOffset.toString(), eventsLimit.toString(), timePeriod, activityType, filter)
      : apiPublicEventsList(eventsOffset.toString(), eventsLimit.toString(), filter);

    return fetcher(apiPath, setIsLoading, { signal });
  };

  const fetchNextEventsPageCount = async (
    eventsOffset: number,
    eventsLimit: number,
    filter: SearchParamsType,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    signal: AbortSignal,
    timePeriod?: EventsTimePeriods,
    activityType?: string,
  ): Promise<EventsNextPageCountResponse> => {
    const reported = filter[SearchParams.filterReported] ? parseInt(filter[SearchParams.filterReported]) : false;

    const apiPath = timePeriod
      ? reported === 1
        ? apiMyReportsNextPageCount(eventsOffset.toString(), eventsLimit.toString(), timePeriod, activityType, filter)
        : apiMyEventsNextPageCount(eventsOffset.toString(), eventsLimit.toString(), timePeriod, activityType, filter)
      : apiPublicEventsNextPageCount(eventsOffset.toString(), eventsLimit.toString(), filter);

    return fetcher(apiPath, setIsLoading, { signal });
  };

  const fetchEventDetails = async (
    id: string,
    isMyEvents: boolean,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<EventDetailsResponse | undefined> => {
    try {
      const apiPath = isMyEvents ? apiMyEventDetails(id) : apiPublicEventDetails(id);

      const errorHandler = () => {
        return;
      };

      return fetcher(apiPath, setIsLoading, {}, errorHandler, 403);
    } catch (err) {
      if (
        !(err instanceof ErrorWithMessageAndResponseStatus) ||
        JSON.parse(JSON.stringify(err)).responseStatus !== 403
      ) {
        throw err;
      }
    }
  };

  const registerToEvent = async (
    id: string,
    payload: RegisterToEventPayload,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    errorHandler: (error: ErrorWithMessageAndResponseStatus) => void,
    statusForErrorHandler: number,
  ) => {
    const apiEndpoint = isAuthenticated ? apiRegisterToEventAsUser(id) : apiRegisterToEventAsGuest(id);

    const options = {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify(payload),
    };

    return fetcher(apiEndpoint, setIsLoading, options, errorHandler, statusForErrorHandler);
  };

  const createEvent = async (
    typeId: string,
    isReport: boolean,
    formData: FormData,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<CreateEventResponse> => {
    return fetcher(apiCreateEvent(typeId, isReport), setIsLoading, {
      method: 'POST',
      body: formData,
    });
  };

  const sendEventDetails = async (
    id: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<CreateEventResponse> => {
    return fetcher(apiSendEventDetails(id), setIsLoading, {
      method: 'POST',
    });
  };

  const updateEvent = async (
    id: string,
    isReport: boolean,
    formData: FormData,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<UpdateEventResponse> => {
    return fetcher(apiEditEvent(id, isReport), setIsLoading, {
      method: 'POST',
      body: formData,
    });
  };

  const fixEvent = async (
    id: string,
    payload: FixEventFormValues,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<UpdateEventResponse> => {
    return fetcher(apiEditEvent(id, false), setIsLoading, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  const deleteEvent = async (id: string, setIsLoading: Dispatch<SetStateAction<boolean>>) => {
    return fetcher(apiDeleteEvent(id), setIsLoading, {
      method: 'DELETE',
    });
  };

  const updateEventAttachment = async (
    id: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    file: File,
  ): Promise<EventAttachmentUpdateResponse> => {
    const presignedUrlResponse = await fetcher(apiEditEventAttachment(id), setIsLoading, {
      method: 'POST',
      body: JSON.stringify({
        attachment_filename: file.name,
      } as UpdateAttachmentPayload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const arrayBuffer = await file.arrayBuffer();
    const blobFile = new Blob([new Uint8Array(arrayBuffer)], { type: file.type });

    return uploadFileToS3(presignedUrlResponse.url, blobFile, setIsLoading);
  };

  const updateEventAttachmentAfterUploadToS3 = async (
    id: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    payload: UpdateAttachmentPayload,
  ) => {
    return fetcher(apiEditEventAttachment(id), setIsLoading, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const removeEventAttachment = async (id: string, setIsLoading: Dispatch<SetStateAction<boolean>>) => {
    return fetcher(apiEditEventAttachment(id), setIsLoading, {
      method: 'DELETE',
    });
  };

  const checkParticipantsEligibility = async (
    id: string,
    emails: string[],
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<CheckingParticipantsEligibilityResponse> => {
    return fetcher(apiValidateEventParticipants(id), setIsLoading, {
      method: 'POST',
      body: JSON.stringify({ emails }),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  const addParticipantsToEventByEmails = async (
    id: string,
    emails: string[],
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<EventParticipantsResponse> => {
    return fetcher(apiAddEventParticipantsByEmails(id), setIsLoading, {
      method: 'POST',
      body: JSON.stringify({ emails }),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  const addParticipantsToEventByFullNames = async (
    id: string,
    payload: AddingEventParticipantsByFullNamesPayload,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<EventParticipantsResponse> => {
    return fetcher(apiAddEventParticipantsByFullNames(id), setIsLoading, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  const sendInviteToEvent = async (
    eventId: string,
    participantId: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ) => {
    return fetcher(apiSendInviteToEvent(eventId, participantId), setIsLoading, {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  const fetchEventParticipants = async (
    id: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<EventParticipantsResponse> => {
    return fetcher(apiEventParticipants(id), setIsLoading);
  };

  const deleteEventParticipant = async (
    eventId: string,
    participantId: string,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
  ): Promise<EventsListResponse> => {
    return fetcher(apiDeleteEventParticipant(eventId, participantId), setIsLoading, {
      method: 'DELETE',
    });
  };

  return {
    fetchEvents,
    fetchNextEventsPageCount,
    fetchEventDetails,
    registerToEvent,
    createEvent,
    sendEventDetails,
    updateEvent,
    fixEvent,
    deleteEvent,
    sendInviteToEvent,
    updateEventAttachment,
    updateEventAttachmentAfterUploadToS3,
    removeEventAttachment,
    checkParticipantsEligibility,
    addParticipantsToEventByEmails,
    addParticipantsToEventByFullNames,
    fetchEventParticipants,
    deleteEventParticipant,
  };
};
